/* momen plugin for goalpilot specific date time conversions */
import Vue from 'vue'
import moment from "moment-timezone/builds/moment-timezone-with-data-2012-2022.min.js"
import momentDurationFormatSetup from "moment-duration-format"
import firebase from "@firebase/app";
import "@firebase/database";
import * as tzHelper from './timezone-helper'

/*******************************************************************************

TODO: Check out Vue.util.defineReactive

TODO: Load tz data async:
    - moment.tz.load(require('./data/packed/latest.json'));
    - check with: moment.tz.zone("UnloadedZone");

TODO: prevent moment from being called directly moment()

*******************************************************************************/

const log = (...args) => { /* console.log(...args) */ };

var localStorage
if ( !process.browser ) {
  localStorage = {
    getItem() { return 'Europe/Berlin' }, /* This will cause a death spiral, when enabled in a browser! */
    setItem() {}
  };
} else {
  localStorage = window.localStorage;
}

export function enhanceMomentJS( moment ) {

  log("[EMO] enhanceMomentJS");

  /* ... */
  moment.isEnhanced = true;
  moment.prototype.isEnhanced = true;

  /* ------------------------------------------------------------------------ */

  // tzHelper.convert_anything_to_localMomentObj();

  moment.fromFirestore = ( firestoreDatetimeObject ) => {
    /* This funcion is mostly called to convert exceptions timestamps to
       moment object. It can probably be used to convert 'neutral' utc
      timestmaps to qualified calendar input.
      ( Also we have to check if it is used anywhere else )
    */

    if ( firestoreDatetimeObject &&
         typeof firestoreDatetimeObject === 'object' &&
         firestoreDatetimeObject.constructor.name === 'Timestamp' ) { // TODO: might not work in production
      return moment( firestoreDatetimeObject.toDate() );
    } else {
      return moment( firestoreDatetimeObject )
    }
  }

  moment.fromRealtimeDB = ( timestamp ) => {
    return moment.unix( timestamp );
  }

  moment.fromRealtimeServerTime = ( timestamp ) => {
    return moment( timestamp );
  }

  moment.fromOptimizer = (datetime) => {
    return moment.unix( datetime ).tz( moment.user_timezone );
  }

  moment.fromDatepicker = (ISOString) => {
    let localMomentObj = tzHelper.convert_usertime_to_localMomentObj(
      ISOString,
      moment.user_timezone
    );
    return localMomentObj
  }

  moment.fromFullCalendarV3 = (momentFullCalendar) => {
    /*
    onSessionSelect:
    MAYBE: if ( checkUTC ) { ... }

    return momentFullCalendar.utcOffset() === 0
      ? tzHelper.convert_calendartime_to_localMomentObj( momentFullCalendar, moment.user_timezone )
      : momentFullCalendar.clone();

    */

    // TODO: Make sure this is an enhacned moment object!

    return tzHelper.convert_calendartime_to_localMomentObj(
      momentFullCalendar,
      moment.user_timezone
    );
  }

  moment.fromFullCalendarV4 = (ISOString) => {
    throw new Error('TODO')
  }

  moment.fromStore = (ISOString) => {
    return moment( ISOString ); /* Deserialize moment object */
  }

  // moment.fromTimeslotGenerator = (date) => {
  // }

  /* ------------------------------------------------------------------------ */

  // TODO: moment.fn. ?

  moment.prototype.toFirestore = function() {
    // TODO: Check timezones
    return firebase.firestore.Timestamp.fromDate( this.toDate() );
  }

  moment.prototype.toRealtimeDB = function() {
    return this.unix();
  }

  moment.prototype.toRealtimeServerTime = function() {
    // We store timestmaps in seconds, while ServerTime is in milliseconds
    return this.valueOf();
  }

  moment.prototype.toBackend = function(backend) {
    return backend === 'REALTIMEDB'
      ? this.toRealtimeDB()
      : this.toFirestore()
  }

  moment.prototype.toOptimizer = function() {
    if ( this.isTimezoneNeutral ) {
      // console.log("Got timezone neutral event for optimizer");
      return this.unix();
    }
    return this.unix();
  }

  moment.prototype.toDatepicker = function() {
    return tzHelper.convert_localMomentObj_to_usertime( this, moment.user_timezone );
  }

  moment.prototype.toFullCalendarV3 = function() {
    // TODO: this is not a timestmap!
    // Converting back and forth not necessary
    // return tzHelper.convert_Timestamp_to_calendartime( this.unix(), moment.user_timezone );
    return this.tz( moment.user_timezone );
  }

  moment.prototype.toFullCalendarV4 = function() {
    return -1;
  }

  moment.prototype.toStore = function () {
    return this.format(); /* Serialize moment object */
  }

  moment.prototype.formatDateLong = function () {
    return this.format(this.user_dateformat); /* Serialize moment object */
  }

  /* ------------------------------------------------------------------------ */

  moment.prototype.isTimezoneNeutral = false; // TODO

  moment.fromDateAndTime = (ISODate, time) => {
    if ( !time ) {
      throw new Error('Not implemented');
    }
    /*
    Moment's string parsing functions like moment(string) and moment.utc(string)
    accept offset information if provided, but convert the resulting Moment object
    to local or UTC time. In contrast, moment.parseZone() parses the string but keeps
    the resulting Moment object in a fixed-offset timezone with the provided offset
    in the string.

    What parseZone really means here, is:
    parseZone => createDateWithoutZone

    Why so complicated?
    */
    // let momentObj = moment.parseZone( ISODate + 'T' + time );

    /* The moment.tz constructor takes all the same arguments as the moment
    constructor, but uses the last argument as a time zone identifier. */
    let momentObj = moment.tz( ISODate + 'T' + time, moment.user_timezone);

    /* This function is used when creating timeslot for a given time and a give date.
       Usually we want to treat time preferences in the user timezone */
    momentObj.isTimezoneNeutral = true;

    return momentObj;
  }

  moment.fromTimezoneNeutral = ( date ) => {
    if ( !moment.isValidDate( date ) ) {
      console.warn("[fromTimezoneNeutral] Invalid date", date)
      return null
    }
    if ( !date.isTimezoneNeutral ) {
      throw new Error('Input is not timezone neutral')
    }
    // TODO: How do we want to handle 'timezone neutral'?
    return tzHelper.convert_timezoneNeutral_to_qualifiedMomentObj( date, moment.user_timezone );
  }

  moment.prototype.changeTimezoneButKeepTime = function(timezone) {
    return tzHelper.convert_timezoneNeutral_to_qualifiedMomentObj( this, timezone );
  }

  /* ------------------------------------------------------------------------ */

  moment.isValidDate = function(obj) {
    return obj &&
           moment.isMoment(obj) &&
           !!obj.isEnhanced &&
           obj.isValid();
    // TODO: move util function here
  }

  moment.currentTime = () => {
    /* WHY: This function should return the same result as moment(), even when
            a custom timezone is set, since we set .locale().
            Using this function is still the prefered way to get the current
            time, because it adds an extra layer of control.
    */
    if ( !moment.user_timezone ) {
      throw new Error('User timezone is not set')
    }

    return moment().tz( moment.user_timezone );
  }

  moment.currentTimeUTC = () => {
    return moment().utc();
  }

  moment.currentDate = ( ) => {
    return moment().tz( moment.user_timezone ).startOf('day');
  }

  moment.currentDateISO = ( ) => {
    moment().tz( moment.user_timezone ).format('YYYY-MM-DD')
  }

  moment.currentDateSleepCorrected = () => {
    const sleepDayBreak = 3 // Daybreak at 3 AM in the morning
    return moment()
            .tz(moment.user_timezone)
            .subtract(sleepDayBreak, 'hours')
            .startOf('day');
  }

  moment.currentCalendarweek = () => {
    return moment().tz( moment.user_timezone ).format('W');
  }

  moment.currentTimeServer = ( backend ) => {
    return backend === 'REALTIMEDB'
           ? firebase.database.ServerValue.TIMESTAMP
           : firebase.firestore.FieldValue.serverTimestamp();
  }

  /* converts 'HH:mm' to fractional time in hours */
  moment.convertHHmmToFractional = (value) => {
    return (moment(value, 'HH:mm') - moment().startOf('day')) / 3600 / 1e3
  }

  /* converts fractional time in hours to 'HH:mm' */
  moment.convertFractionalToHHmm = (value) => {
    return moment.utc( value * 3600 * 1e3 ).format('HH:mm')
  }

  if ( process.browser ) {
    window.moment = moment;
  }
}

export function attachTimezoneWatcher( moment, store ) {

    log("[EMO] attaching timezone watcher");

    /* Install watcher in store and wait until we get the user timezone. This
      also means that the store is now initialized and was using a moment
      object with a potentially different timezone... we have to take care of
      that */
    let unwatch_user_get_timezone = store.watch(
      (state, getters) => getters["app/user_get_timezone"],
      user_timezone => {

        log("[EMO] got new user_timezone", user_timezone);

        // TODO: The user might not have a timezone configured, in this case
        //       we use the timezone of the operating system, which is perfectly
        //       fine.

        if ( user_timezone === 'I/DONT/KNOW' ) {
          /* HACK: we don't know the TZ yet - do nothing */
          // TODO: null
          return;
        }

        if ( localStorage.getItem('timezone') !== user_timezone ) {
          /* We have a problem: The user has a different timezone then we
            assumed, this means the store is fucked up */
          console.warn("[EMO] locally saved timezone does not match user timezone",
            localStorage.getItem('timezone'), "!=", user_timezone);

          /* Update local storage */
          localStorage.setItem('timezone', user_timezone);

          // TODO: Ask the user if we should set his timezone to moment.tz.guess,
          //       but only ask once

          // TODO: Also we should check if the users operating system changed
          //       the timezone (e.g. travel)
          //       We can do this by further checking if
          //        localStorage.timezone  !== moment.tz.guess
          //       Again, we should only ask once after every change and only
          //       if the user has a timezone configured.

          if ( location /* check if this is a browser */ ) {
            // TODO: Make absolutly sure, that we don't end up in a death sprial */
            location.reload()
          } else {
            throw new Error('Timezone mismatch detected. Timezone consistency can not be guaranteed.')
          }
        }

        log("[EMO] Setting user timezone in moment object");

        /* Set default timezone */
        moment.tz.setDefault(user_timezone)
        // moment.tz.setDefault("Europe/Berlin"); // Don't do that, see FAQ
        // moment.tz.setDefault(String);

        // TODO: This only works because it is fast
        // but moment.user_timezone is not reactive!
        moment.user_timezone = user_timezone;
        // Either create getter that will tell templates when tz is ready
        // or delay everything

        // TODO: Testen...
        Vue.util.defineReactive(moment, 'user_timezone', user_timezone, null, true)

        // log("[EMO] unwatch_user_get_timezone", unwatch_user_get_timezone);
        if ( unwatch_user_get_timezone ) {
          log("[EMO] Self destroying watcher");
          unwatch_user_get_timezone(); // Self destroy
        }
      },
      { immediate: true }
    );
    /// or ... store.subscribe((mutation, state))

    // moment.stopWatcher = () => {
    //   // TODO: geht nicht bei mehreren enhances
    //   unwatch();
    //   // delete moment.isEnhanced;
    //   // ...
    // };
}

export function attachDateformatWatcher( moment, store ) {

  /* TODO: Might be unnecessary, see: locale-filter.js

  log("[EMO] attaching dateformat watcher");

  // See attachTimezoneWatcher
  let unwatch_user_get_dateformat = store.watch(
    (state, getters) => getters["app/user_get_dateformat"],
    user_dateformat => {

      log("[EMO] got new user_dateformat", user_dateformat);
      log("[EMO] Setting user timezone in moment object");

      // Set default dateformat
      moment.tz.setDefault(user_dateformat)
      moment.user_dateformat = user_dateformat;

      Vue.util.defineReactive(moment, 'user_dateformat', user_dateformat, null, true)

      if ( unwatch_user_get_dateformat ) {
        log("[EMO] Self destroying watcher");
        unwatch_user_get_dateformat(); // Self destroy
      }
    },
    { immediate: true }
  );
  */
}

export let localeSetUp = (function () {

  log("[EMO] set up");
  log("[EMO] localStorage.getItem('timezone')", localStorage.getItem('timezone'));

  if ( !localStorage.getItem('timezone') ) {
    /* Let's be optimistic and assume that the user configured the same timezone
       that moment.tz.guess will give us. If not we deal with that later... */
    localStorage.setItem('timezone', moment.tz.guess());
  }

  /* Set default values */
  let user_timezone = localStorage.getItem('timezone');
  log("[EMO] Setting user timezone to", user_timezone);
  moment.tz.setDefault(user_timezone)
  moment.user_timezone = user_timezone;

  // moment.locale("en"); // -> i18n

  momentDurationFormatSetup( moment );
  enhanceMomentJS( moment );

  // export default function(...args) {
  //   let enhancedMoment = moment(...args).tz('Europe/Berlin');
  //   // enhanceMomentJS( enhancedMoment );
  //   return enhancedMoment;
  // }

})();

export default moment;

// moment.duration....

/*
moment.updateLocale('en', {
    longDateFormat : {
        LT: "h:mm A",
        LTS: "h:mm:ss A",
        L: "MM/DD/YYYY",
        l: "M/D/YYYY",
        LL: "MMMM Do YYYY",
        ll: "MMM D YYYY",
        LLL: "MMMM Do YYYY LT",
        lll: "MMM D YYYY LT",
        LLLL: "dddd, MMMM Do YYYY LT",
        llll: "ddd, MMM D YYYY LT"
    }
});
*/

 // eslint-disable-next-line
function getDateOfISOWeek(w, y) {
  // DEPRECATED
  let simple = new Date(y, 0, 1 + (w - 1) * 7);
  let dow = simple.getDay();
  let ISOweekStart = simple;
  if (dow <= 4) {
    ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
  } else {
    ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
  }
  return ISOweekStart;
}

// navigator.geolocation.getCurrentPosition((x) => console.log(x.coords))

// import moment from '@/moment-gp.js';
// export default moment().tz('foo');
// and
// import momentFoo from '../somewhere/moment-foo';

// TODO: integrate: https://github.com/julon/vue-moment-lib

// import yourMoment from "moment";
// import VueMomentLib from "vue-moment-lib";
//
// //
// // customize your moment instance here (locales, config, etc)
// //
//
// // Install this library with custom moment instance
// Vue.use(VueMomentLib, { moment: yourMoment });
